/**
 * Firm-wide list of variable-rate bonds for the Manual "Update Variable Rate
 * Bonds" view.
 *
 * Read-through cache: fetched and memoized so the list is warm at login
 * (preloadVariableRateBonds fires from MainLayout) and the Manual view renders
 * with zero fetch; re-read when stale (see utils/cacheFreshness). The cache is
 * in-memory module state (never persisted) and is cleared on logout so a second
 * user on the same tab never inherits the previous user's holdings.
 */
import { API_ENDPOINTS } from '../../../config/api';
import { refreshIfStale } from '../../../utils/cacheFreshness';

function authHeader() {
  const token =
    localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

let cache = null; // resolved variable-rate bonds array
let cachedAt = 0; // when `cache` was last read from the backend
let inflight = null; // dedupes concurrent callers
let epoch = 0; // bumped on clear so a stale in-flight response can't repopulate the cache

/**
 * Resolve the variable-rate bonds list, reusing the cache (or an in-flight
 * request). Pass `{ force: true }` to bypass the cache and refetch (Refresh).
 * Pass `fetcher` (AuthContext's fetchWithAuth) for 401 -> refresh -> retry;
 * the default raw fetch is only for the login-time preload (fresh token).
 */
export function loadVariableRateBonds({ force = false, fetcher } = {}) {
  if (!force && cache) return Promise.resolve(cache);
  if (!force && inflight) return inflight;
  const started = epoch;
  const doFetch = fetcher || ((url) => fetch(url, { headers: authHeader() }));
  const request = doFetch(API_ENDPOINTS.manual.variableRateBonds)
    .then((res) => {
      if (!res.ok) throw new Error(`variable-rate-bonds ${res.status}`);
      return res.json();
    })
    .then((data) => {
      if (epoch === started) {
        cache = data;
        cachedAt = Date.now();
        inflight = null;
      }
      return data;
    })
    .catch((err) => {
      if (epoch === started) inflight = null;
      throw err;
    });
  inflight = request;
  return request;
}

/** Warm the cache at login (fire-and-forget; errors are swallowed). */
export function preloadVariableRateBonds() {
  loadVariableRateBonds().catch(() => {});
}

/** The freshness refresher for this cache (see utils/cacheFreshness). */
export function refreshStaleVariableRateBonds(fetcher) {
  refreshIfStale({ loaded: cache, busy: inflight, readAt: cachedAt, reload: loadVariableRateBonds }, fetcher);
}

/** Drop the cached bonds (call on logout / token clear). */
export function clearVariableRateBonds() {
  cache = null;
  inflight = null;
  epoch += 1;
}

// --- Timeline + entry + classification mutations (no cache interaction) ----
//
// Thin wrappers over the injected `fetcher` (AuthContext's fetchWithAuth -
// 401 -> refresh -> retry). They do NOT touch the list cache; the widget calls
// clearVariableRateBonds() explicitly after a mutation (spec section 7).

async function asJson(promise) {
  const res = await promise;
  if (!res.ok) throw new Error(`variable-rate-bonds ${res.status}`);
  return res.json();
}

/** GET the merged CUSIP-anchored timeline for one security (fetcher = fetchWithAuth). */
export function getTimeline(securityId, fetcher) {
  return asJson(fetcher(API_ENDPOINTS.manual.vrbTimeline(securityId)));
}

/** POST a new manual entry; resolves the created entry row. */
export function postEntry(securityId, body, fetcher) {
  return asJson(
    fetcher(API_ENDPOINTS.manual.vrbEntries(securityId), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
  );
}

/** PUT an edit to one manual entry (soft-delete + insert server-side). */
export function putEntry(securityId, entryId, body, fetcher) {
  return asJson(
    fetcher(API_ENDPOINTS.manual.vrbEntry(securityId, entryId), {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
  );
}

/** DELETE (soft) one manual entry; resolves the delete acknowledgement. */
export function deleteEntry(securityId, entryId, fetcher) {
  return asJson(
    fetcher(API_ENDPOINTS.manual.vrbEntry(securityId, entryId), { method: 'DELETE' })
  );
}

/** PUT the tri-state classification for a CUSIP; resolves the updated classification row. */
export function putClassification(cusip, body, fetcher) {
  return asJson(
    fetcher(API_ENDPOINTS.manual.vrbClassification(cusip), {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
  );
}
